import React, { Fragment, useContext, useEffect, useState } from 'react';
import Header from '../../../components/Common/Header/Header';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import ContentApp from '../../../components/ContentApp';
import { fetchReservation } from '../../../helpers/reservation';
import { WelletContext } from '../../../context/WelletContext';
import { getQueryString } from '../../../helpers/url';
import useForm from '../../../helpers/useForm';
import ButtonProccessing, {
  ButtonStatus,
} from '../../../components/Common/ButtonProccessing/ButtonProccessing';
import { isValidPhoneNumber } from 'react-phone-number-input';
import EventHeader from '../../../components/Common/EventHeader/EventHeader';
import ReservationCheckCustomerInfo from '../../../components/ReservationCheck/ReservationCheckCustomerInfo/ReservationCheckCustomerInfo';
import styles from './reservationCheck.module.css';
import { CircleSpinner } from 'react-spinners-kit';
import { convertTime12to24, getDatePerformance } from '../../../helpers/dates';
import CustomerSpecialRequest from '../../../components/ReservationCheck/CustomerSpecialRequest/CustomerSpecialRequest';
import ErrorBox from '../../../components/Common/ErrorBox/ErrorBox';
import ContentBottom from '../../../components/ContentBottom';
import PerformanceSelector from '../../../components/PerformanceSelector/PerformanceSelector';
import EmptyActivity from '../../../components/Common/EmptyActivity/EmptyActivity';
import OccasionSelectorButton from '../../../components/ReservationCheck/OccasionSelectorButton/OccasionSelectorButton';
import SummaryProducts from '../../../components/ReservationCheck/Summary/SummaryProducts';
import Summary from '../../../components/ReservationCheck/Summary/Summary';
import InputFormHolder from '../../../components/Common/InputFormBorder/InputFormBorder';

const ReservationCheckGroup = ({ history }) => {
  const { t } = useTranslation();
  const reservation = useSelector((state) => state.reservation);
  const currency = useSelector((state) => state.currency.selected.shortForm);
  const idReservation = getQueryString('id');
  const countryCode = useSelector((state) => state.app.currentMarket.countryCode);
  const marketId = useSelector((state) => state.app.currentMarket.marketSetupId);
  const extraHours = useSelector((state) => state.extraProducts.hours);
  const welletContext = useContext(WelletContext);
  const language = useSelector((state) => state.app.language);

  const [loadingReservation, setLoadingReservation] = useState(true);
  const [reservationInfo, setReservationInfo] = useState(null);
  const [originalReservation, setOriginalReservation] = useState(null);
  const [paxs, setPaxs] = useState(reservation.paxs);
  const hasProducts = reservation.products?.length > 0;
  const [btnStatus, setBtnStatus] = useState(ButtonStatus.Normal);
  const [selectedButton, setSelectedButton] = useState(null);
  const [initialNoAvailabilityError, setInitialNoAvailabilityError] = useState(null);
  const [selectedPerformance, setSelectedPerformance] = useState(null);
  const [error, setError] = useState(null);
  const [phoneError, setPhoneError] = useState('');
  const [reservationDate, setReservationDate] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loadingSchedule, setLoadingSchedule] = useState(true);
  const [intervals, setIntervals] = useState([]);

  const handleSelectOccasion = (value) => {
    if (selectedButton === value) {
      setSelectedButton(null);
      handleChange(undefined, 'occasion', null);
    } else {
      handleChange(undefined, 'occasion', value);
      setSelectedButton(value);
    }
  };

  const getReservation = async () => {
    let reservationPreview = null;
    const updatedReservation = {
      ...reservation,
      products: (reservation.products ?? []).map((product) => {
        if (product.specialType === 'OPEN_BAR_HOURS' && extraHours > 0) {
          return { ...product, hours: extraHours };
        }
        return product;
      }),
    };

    const result = await fetchReservation({
      welletContext,
      reservation: updatedReservation,
      language,
      refReservationId: idReservation,
      pickup: reservation.transportation,
      customerCurrency: currency,
    });

    if (result.success) {
      const data = result.data;
      const updatedProducts = (data.products ?? []).map((product) => {
        if (product.specialType === 'OPEN_BAR_HOURS' && extraHours > 0) {
          return { ...product, hours: extraHours };
        }
        return product;
      });

      const reservationWithProducts = {
        ...data,
        products: updatedProducts,
      };

      reservationPreview = reservationWithProducts;
      setReservationInfo(reservationWithProducts);

      if (reservation?.date) {
        setReservationDate(new Date(reservation.date + 'T00:00:00'));
      }
    } else {
      console.error(result.error);
    }

    setLoadingReservation(false);
    return reservationPreview;
  };

  const getOriginalReservation = async () => {
    try {
      const response = await welletContext.apis.tickets.get(
        `/Reservation/${idReservation}?lang=${language}`,
      );
      setOriginalReservation(response.data);
      setInitialValues(response.data);
      setPaxs(response.paxs);
    } catch (error) {
      console.error(error);
    }
  };

  const getSchedule = async () => {
    setLoadingSchedule(true);

    try {
      const result = await welletContext.apis.tickets.get(
        `/shows/GetSchedule?showId=${reservationInfo.show.id}&reservationType=GROUP&date=${reservation.date}`,
      );

      if (result.data.schedules.length === 0) {
        setIntervals([]);
      }

      const formattedTimes = result.data.schedules.map((time) => {
        const time24 = new Date(reservation.date + 'T' + convertTime12to24(time.timeStart));
        if (time.timeMinutesStart >= 0 && time.timeMinutesStart <= 360)
          time24.setDate(time24.getDate() + 1);
        return { time24, soldOut: time.isSoldOut };
      });
      formattedTimes.sort();

      const allIntervals = formattedTimes.map(({ time24, soldOut }) => ({
        time: time24,
        soldOut,
      }));

      setIntervals(allIntervals);
      setLoadingSchedule(false);
    } catch (error) {
      console.error(error);
    }
  };

  const setInitialValues = (reservation) => {
    handleChange(null, 'customerName', reservation.customerFirstName);
    handleChange(null, 'customerLastname', reservation.customerLastName);
    handleChange(null, 'phone', reservation.customerPhone);
  };

  useEffect(() => {
    if (reservation.date && reservation.performance) {
      setReservationDate(new Date(reservation.date + 'T' + reservation.performance));
    }

    if (hasProducts) {
      getReservation();
    }

    if (idReservation) getOriginalReservation();
  }, []);

  useEffect(() => {
    if (reservationInfo) {
      getSchedule();
    }
  }, [reservationInfo]);

  const sendForm = async () => {
    if (reservationInfo.totalAmount === 0 && !isModalOpen) {
      setIsModalOpen(true);
      return;
    } else {
      setBtnStatus(ButtonStatus.InProgress);

      try {
        let url = '/reservation/GenerateReservation';
        if (reservationInfo && idReservation) {
          url = '/reservation/modifyreservation';
        }

         let reservationLocalTime = '';

    if (selectedPerformance != null)
      reservationLocalTime = reservation.date + 'T' + selectedPerformance;
    else reservationLocalTime = getDatePerformance(reservation.date, reservation.performance);

        const result = await welletContext.apis.tickets.put(url, {
          showId: reservation.showId,
          paxs: paxs,
          customer: {
            firstName: values.customerName,
            lastName: values.customerLastname,
            phone: values.phone,
            email: values.email,
          },
          comments: values.comments ?? null,

          occasion: null,
          countryCode: values.countryCode ?? 'MX',
          reservationDateLocalTime: reservationLocalTime,
          totalAmount: reservationInfo?.totalAmount ?? null,
          products:
            reservation.products && reservation.products.length > 0
              ? reservation.products.map((product) => ({
                  ...product,
                  ...(product.specialType === 'OPEN_BAR_HOURS' && extraHours > 0
                    ? { hours: extraHours }
                    : {}),
                }))
              : null,
          oldReservationId: idReservation,
          language: language,
          pickup: reservation.transportation,
          customerCurrency: currency,
          eventName: values.eventName ?? null,
        });

        setBtnStatus(ButtonStatus.ProgressFinished);
        setTimeout(() => {
          history.push(
            reservationInfo.totalAmount === 0
              ? '/reservation-success-courtesy'
              : `/reservation-success?reservationType=${result.data.reservationType}&status=${result.data.status}`,
          );
        }, 1500);
      } catch (error) {
        setBtnStatus(ButtonStatus.Error);
        let errorMessage;
        switch (error.response.data.errorType) {
          case 'GENERIC_ERROR':
            errorMessage = t('reservationError.GENERIC_ERROR');
            break;
          case 'SHOW_NOT_FOUND':
            errorMessage = t('reservationError.SHOW_NOT_FOUND');
            break;
          case 'SCHEDULE_SOLD_OUT':
            errorMessage = t('reservationError.SCHEDULE_SOLD_OUT');
            break;
          case 'INVALID_RESERVATION_DATE':
            errorMessage = t('reservationError.INVALID_RESERVATION_DATE');
            break;
          case 'NO_AVAILABILITY':
            errorMessage = t('reservationError.NO_AVAILABILITY');
            break;
          case 'DUPLICATE_RESERVATION':
            errorMessage = t('reservationError.DUPLICATE_RESERVATION');
            break;
          case 'SALES_EVENT_END':
            errorMessage = t('reservationError.NO_AVAILABILITY');
            break;
          case 'PROMOTION_ERROR':
            errorMessage = t('promotionError.' + error.response.data.message);
            break;
          case 'PURCHASE_RULE_NOT_SATISFIED':
            errorMessage = t('reservationError.PURCHASE_RULE_NOT_SATISFIED');
            break;
          default:
            errorMessage = t('reservationError.GENERIC_ERROR');
        }
        setError(errorMessage);
      }
    }
  };

  function validate(values) {
    let errors = {};
    if (!originalReservation) {
      if (!values.customerName) {
        errors.customerName = t('nameError.EMPTY_NAME');
      } else if (/\d/.test(values.customerName)) {
        errors.customerName = t('nameError.NOT_NUMBERS');
      }

      if (!values.email) {
        errors.email = t('emailError.EMPTY_EMAIL');
      } else if (!/\S+@\S+\.\S+/.test(values.email)) {
        errors.email = t('emailError.INVALID_EMAIL');
      }

      if (!values.customerLastname) {
        errors.customerLastname = t('nameError.EMPTY_LASTNAME');
      } else if (/\d/.test(values.customerLastname)) {
        errors.customerLastname = t('nameError.NOT_NUMBERS');
      }

      if (!values.phone) {
        errors.phone = t('phoneError.EMPTY_PHONE_NUMBER');
        setPhoneError(errors.phone);
      } else {
        setPhoneError('');
      }

      if (values.phone && !isValidPhoneNumber(values.phone)) {
        errors.phone = t('phoneError.INVALID_PHONE_NUMBER');
        setPhoneError(errors.phone);
      }
    }

    if (!selectedPerformance) {
      errors.performance = t('selectArrivalTime');
    }

    return errors;
  }

  let { values, errors, handleChange, handleSubmit } = useForm(sendForm, validate);

  return (
    <>
      {loadingReservation ? (
        <div className='loaderDiv'>
          <CircleSpinner
            className='spinner-app mx-auto'
            style={{ margin: 'auto' }}
            size={40}
            color='var(--color-font-primary)'
          />
        </div>
      ) : (
        <>
          <Header />
          <ContentApp classes='pb-0'>
            {initialNoAvailabilityError ? (
              <>
                <ErrorBox title={initialNoAvailabilityError} />
                <div className='mt-4 text-center text-primary' onClick={() => history.goBack()}>
                  {t('reschedule')}
                </div>
              </>
            ) : (
              <>
                <EventHeader
                  eventImg={reservationInfo?.show?.bannerImageUrl}
                  eventName={reservationInfo?.show?.name}
                  eventDate={reservation?.date}
                  size='small'
                />

                <div className='mt-4'>
                  <SummaryProducts reservationInfo={reservationInfo} />
                </div>

                <div className='mt-4'>
                  <Summary reservation={reservationInfo} />
                </div>

                <Fragment>
                  <>
                    <div className='fw-semibold text-primary font-tiny mt-4 mb-2'>
                      {t('reservationInformation')}
                    </div>
                    <ReservationCheckCustomerInfo
                      handleChange={handleChange}
                      values={values}
                      errors={errors}
                      phoneError={phoneError}
                      setPhoneError={setPhoneError}
                      countryCode={countryCode}
                    />

                    {!intervals ? (
                      <div className='mt-3'>
                        <EmptyActivity title={t('noSchedule')} />
                      </div>
                    ) : (
                      <div className='position-relative'>
                        <div>
                          <span className='fw-semibold text-primary font-tiny mt-4 mb-2'>
                            {t('eventInformation')}
                          </span>
                          <div className='mt-2'>
                            <InputFormHolder
                              type='text'
                              name='eventName'
                              onChange={handleChange}
                              value={values.eventName}
                              error={errors.eventName}
                              placeholder={t('eventName')}
                            />
                          </div>
                          <span className='fw-semibold text-primary font-tiny mt-4 mb-2'>
                            {t('startTimeEvent')}
                          </span>
                          <div className='mt-3'>
                            <PerformanceSelector
                              performances={intervals}
                              onHandleChange={setSelectedPerformance}
                              defaultPerformance={selectedPerformance}
                            />
                          </div>
                        </div>
                        <div className='pb-2'>
                          {errors.performance && errors.performance.length > 0 && (
                            <div className='error-label'>{errors.performance}</div>
                          )}
                        </div>
                      </div>
                    )}
                    {marketId !== 581 && (
                      <div className='mt-4'>
                        <OccasionSelectorButton
                          handleSelectButton={handleSelectOccasion}
                          selectedButton={selectedButton}
                        />
                      </div>
                    )}

                    <div className='mt-4'>
                      <CustomerSpecialRequest values={values} handleChange={handleChange} />
                    </div>
                  </>

                  {error && (
                    <div className='mt-4'>
                      <>
                        <ErrorBox title={error} />
                        <div
                          className='mt-4 text-center text-primary'
                          onClick={() => history.goBack()}
                        >
                          {t('reschedule')}
                        </div>
                      </>
                    </div>
                  )}
                </Fragment>
              </>
            )}
          </ContentApp>

          <ContentBottom>
            <div className='mt-4'>
              <ButtonProccessing
                status={btnStatus}
                text={reservationInfo.totalAmount === 0 ? t('generateTickets') : t('sendLink')}
                size='large'
                className={styles.btn}
                type='submit'
                onClick={handleSubmit}
                arrow
                color='#000'
                backgroundColor='var(--color-primary)'
                iconColor='#000'
              />
            </div>
          </ContentBottom>
        </>
      )}
    </>
  );
};

export default ReservationCheckGroup;
